import React from 'react';
import styles from './DocumentListVault.module.scss';
import { Box, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CalendarIcon from '../../../../../assets/svg/calendar_month.svg?react';
import { Helpers } from '../../../../common/helpers/helpers';
import { useDocumentsFunctions } from '../../hooks/useDocumentsFunctions';
import EmployeeIcon from '@mui/icons-material/BadgeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CustomersIcon from '@mui/icons-material/SupportAgentOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import PdfIcon from '../../../../../assets/svg/pdf_icon.svg?react';
import DocxIcon from '../../../../../assets/svg/docx_icon.svg?react';
import BKIcon from '../../../../../assets/svg/bk_icon.svg?react';
import { DocumentsListRowProps } from './types';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../../common/constants/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import { sanitizeRedirectUrl } from '../../../../common/helpers/sanitize';
import { USER_PERMISSIONS } from '../../../../common/constants/permissions';
import { useCheckPermission } from '../../../../common/hooks/useCheckPermission';
import { useDocumensoRedirect } from '../../../../common/hooks/useDocumensoRedirect';

export const DocumentsListRow = ({ doc, renderActionButtons }: DocumentsListRowProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const checkPermission = useCheckPermission();
  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();
  const path = location.pathname;
  const {
    getWorkflow,
    getFileTypeFromName,
    getProfileName,
    getProfileType,
    navigateToDocumentPageTab,
  } = useDocumentsFunctions();
  const FileTypeLabel = ({ type, tooltipText }: { type: string; tooltipText: string }) => (
    <Tooltip title={tooltipText || type} arrow placement='top'>
      <span
        style={{
          display: 'flex',
          width: '42px',
          height: '20px',
          justifyContent: 'center',
          alignItems: 'center',
          fontFamily: '"Medium Inter", sans-serif',
          fontSize: '12px',
          color: '#484848',
          backgroundColor: '#92E9E4',
          borderRadius: '4px',
          textTransform: 'uppercase',
        }}
      >
        {type}
      </span>
    </Tooltip>
  );
  const renderProfileIcon = (profileType: string) => {
    switch (profileType) {
      case 'employment':
        return <EmployeeIcon fontSize='small' />;
      case 'supplier':
        return <BusinessCenterOutlinedIcon fontSize='small' />;
      case 'customer':
        return <CustomersIcon fontSize='small' />;
      case 'business':
        return <ApartmentOutlinedIcon fontSize='small' />;
      default:
        return null;
    }
  };
  const renderFileTypeIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return (
          <Tooltip title={t('fileTypes.pdf')} arrow placement='top'>
            <span>
              <PdfIcon />
            </span>
          </Tooltip>
        );
      case 'docx':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'doc':
        return (
          <Tooltip title={t('fileTypes.msWord')} arrow placement='top'>
            <span>
              <DocxIcon />
            </span>
          </Tooltip>
        );
      case 'bk':
        return (
          <Tooltip title={t('fileTypes.businessKitz')} arrow placement='top'>
            <span>
              <BKIcon />
            </span>
          </Tooltip>
        );
      case 'png':
        return <FileTypeLabel type='png' tooltipText={t('fileTypes.image')} />;
      case 'jpg':
        return <FileTypeLabel type='jpg' tooltipText={t('fileTypes.image')} />;
      case 'jpeg':
        return <FileTypeLabel type='jpeg' tooltipText={t('fileTypes.image')} />;
      case 'heic':
        return <FileTypeLabel type='heic' tooltipText={t('fileTypes.image')} />;
      default:
        return null;
    }
  };
  const profileType = getProfileType(doc.category);
  const profileIcon = renderProfileIcon(profileType);
  const fileTypeIcon = renderFileTypeIcon(
    'du_filetype' in doc
      ? doc.du_filetype.length > 0
        ? doc.du_filetype[0]
        : getFileTypeFromName(doc.name, doc.contract_generation)
      : 'bk'
  );
  const timeDifference = Date.now() - new Date(doc.created).getTime();
  const navigate = useNavigate();
  const checkDocUploadOrContractReadingPermission = () => {
    if ('contract_generation' in doc) {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_CONTRACT_READ);
    } else {
      return checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_READ);
    }
  };
  return (
    <TableRow className={`${styles.table_row} ${timeDifference < 300000 ? styles.doc_new : ''}`}>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Box className={styles.file_name_container}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {doc.name.endsWith('.pdf') || doc.name.endsWith('.docx') || doc.name.endsWith('.doc')
                ? doc.name.replace('.pdf', '').replace('.docx', '').replace('.doc', '')
                : doc.name}{' '}
            </Box>
            {timeDifference < 300000 ? (
              <Box className={styles.badge_new} sx={{ marginLeft: '10px' }}>
                {t('labels.new')}
              </Box>
            ) : null}
          </Box>
        </Box>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Box className={styles.profile_container}>
          {profileIcon && <Box className={styles.profile_icon}>{profileIcon}</Box>}
          <Typography variant='body2' className={styles.profile_text}>
            {getProfileName(profileType, doc).join(', ')}
          </Typography>
        </Box>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Typography variant='body2' className={styles.cell_text}>
          {getWorkflow(doc)}
        </Typography>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Box className={styles.date_container}>
          <CalendarIcon />
          <Typography variant='body2' className={styles.cell_text}>
            {Helpers.timeStampToDateV2(doc.updated)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Box className={styles.date_container}>
          {doc.key_date_end_date ? (
            <>
              <CalendarIcon />
              <Typography variant='body2' className={styles.cell_text}>
                {Helpers.timeStampToDateV2(doc.key_date_end_date)}
              </Typography>
            </>
          ) : (
            '-'
          )}
        </Box>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        <Box className={styles.file_type_container}>
          {fileTypeIcon && <Box className={styles.file_type_icon}>{fileTypeIcon}</Box>}
        </Box>
      </TableCell>
      <TableCell
        className={styles.table_cell}
        onClick={
          !doc.status && checkDocUploadOrContractReadingPermission()
            ? () => navigateToDocumentPageTab(doc, 0)
            : null
        }
      >
        {doc.status === 'COMPLETED' ||
        (doc.extras.length > 0 &&
          typeof doc.extras[0].is_signed === 'boolean' &&
          doc.extras[0].is_signed) ? (
          <Tooltip
            title={
              doc.signing_doc_id ? t('messages.signingSignedStatus') : t('messages.markedAsSigned')
            }
            arrow
            placement='top'
          >
            <Box
              className={styles.green_label}
              tabIndex={-1}
              onClick={
                doc.signing_doc_id
                  ? () =>
                      redirectToDocumenso(
                        `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}&activeTab=${encodeURIComponent(doc.category)}`
                      )
                  : null
              }
            >
              {t('labels.signed')}
            </Box>
          </Tooltip>
        ) : doc.status === 'PENDING' ? (
          <Tooltip title={t('messages.signingPendingStatus')} arrow placement='top'>
            <Box
              className={styles.blue_label}
              tabIndex={-1}
              onClick={
                doc.signing_doc_id
                  ? () =>
                      redirectToDocumenso(
                        `${ROUTES.DOCUMENTS}/${doc.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}&activeTab=${encodeURIComponent(doc.category)}`
                      )
                  : null
              }
            >
              {t('labels.pending')}
            </Box>
          </Tooltip>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell className={styles.table_cell}>{renderActionButtons(doc)}</TableCell>
    </TableRow>
  );
};
